import type { Rect } from '@/types/ainotebook'
import { hexToRgb, rgbToHex } from './color'

export function fillWhite(ctx: CanvasRenderingContext2D, w: number, h: number) {
  ctx.save()
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, w, h)
  ctx.restore()
}

function rgbDist(a: { r: number; g: number; b: number }, r: number, g: number, b: number) {
  const dr = a.r - r, dg = a.g - g, db = a.b - b
  return Math.sqrt(dr * dr + dg * dg + db * db)
}

export function pickBackgroundColorRing(
  baseCtx: CanvasRenderingContext2D,
  rect: Rect,
  textColorHex?: string
) {
  const W = baseCtx.canvas.width
  const H = baseCtx.canvas.height

  const t = Math.max(2, Math.round(Math.min(rect.w, rect.h) * 0.12))
  const pad = Math.max(2, Math.round(t * 0.8))

  const x0 = Math.floor(rect.x)
  const y0 = Math.floor(rect.y)
  const x1 = Math.floor(rect.x + rect.w)
  const y1 = Math.floor(rect.y + rect.h)

  const strips: Rect[] = [
    { x: x0, y: y0 - pad - t, w: rect.w, h: t },
    { x: x0, y: y1 + pad, w: rect.w, h: t },
    { x: x0 - pad - t, y: y0, w: t, h: rect.h },
    { x: x1 + pad, y: y0, w: t, h: rect.h }
  ].map(s => ({
    x: Math.max(0, Math.floor(s.x)),
    y: Math.max(0, Math.floor(s.y)),
    w: Math.max(1, Math.floor(Math.min(W - Math.max(0, Math.floor(s.x)), s.w))),
    h: Math.max(1, Math.floor(Math.min(H - Math.max(0, Math.floor(s.y)), s.h)))
  })).filter(s => s.w > 1 && s.h > 1)

  if (!strips.length) return '#ffffff'

  const textRgb = textColorHex ? hexToRgb(textColorHex) : null

  const hist = new Map<number, { count: number; r: number; g: number; b: number }>()
  for (const s of strips) {
    const img = baseCtx.getImageData(s.x, s.y, s.w, s.h).data
    const step = Math.max(1, Math.floor(Math.min(s.w, s.h) / 10))

    for (let yy = 0; yy < s.h; yy += step) {
      for (let xx = 0; xx < s.w; xx += step) {
        const i = (yy * s.w + xx) * 4
        const r = img[i], g = img[i + 1], b = img[i + 2], a = img[i + 3]
        if (a < 40) continue
        if (textRgb && rgbDist(textRgb, r, g, b) < 28) continue

        const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4)
        const hit = hist.get(key)
        if (hit) {
          hit.count++
          hit.r += r; hit.g += g; hit.b += b
        } else {
          hist.set(key, { count: 1, r, g, b })
        }
      }
    }
  }

  if (!hist.size) return '#ffffff'

  let best = { count: -1, r: 0, g: 0, b: 0 }
  for (const v of hist.values()) {
    if (v.count > best.count) best = v
  }
  const r = Math.round(best.r / best.count)
  const g = Math.round(best.g / best.count)
  const b = Math.round(best.b / best.count)
  return rgbToHex(r, g, b)
}

export function drawTextInRect(
  ctx: CanvasRenderingContext2D,
  baseCtx: CanvasRenderingContext2D,
  text: string,
  rect: Rect,
  color: string,
  fontScale: number
) {
  const safeText = (text || '').trim()
  if (!safeText) return

  const pad = Math.max(1, rect.h * 0.08)
  const fontSize = Math.max(10, rect.h * 0.95) * (fontScale / 100)

  ctx.font = `${fontSize}px system-ui, -apple-system, Segoe UI, Roboto, Arial`
  const textWidth = ctx.measureText(safeText).width
  const minWidth = textWidth + pad * 2

  const actualRect = { ...rect }
  if (minWidth > rect.w) actualRect.w = minWidth

  const bg = pickBackgroundColorRing(baseCtx, actualRect, color)
  ctx.fillStyle = bg
  ctx.fillRect(actualRect.x, actualRect.y, actualRect.w, actualRect.h)

  const textColor = color && color !== '' ? color : '#000000'
  ctx.fillStyle = textColor
  ctx.font = `${fontSize}px system-ui, -apple-system, Segoe UI, Roboto, Arial`
  ctx.textBaseline = 'middle'
  ctx.textAlign = 'left'

  const textX = actualRect.x + pad
  const centerY = actualRect.y + actualRect.h / 2
  ctx.fillText(safeText, textX, centerY)
}
